// 🔒 Memoized DetailsModal to isolate heavy AI/timeline rendering
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Container,
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  Button,
  ButtonGroup,
  Grid,
  TextField,
  LinearProgress,
  Divider,
  IconButton,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const DetailsModal = React.memo(function DetailsModal({
  open,
  selected,
  closeDetails,

  // timeline + activity
  logEvent,
  fetchTimeline,
  timelineLoading,
  setTimelineLoading,

  // AI summary
  aiSummary,
  aiSummaryLoading,
  aiSummaryError,

  // notes input
  noteText,
  setNoteText,

  // actions / handlers
  handleGenerateScript,
  callScript,
  callScriptError,
  callScriptLoading,
  copyToClipboard,
  canGenerateScript,
  handleOpenSkipTrace,
  toggleBrad,
  bradKeys,
  makeKey,
  deleting,
  handleDeleteFromDB,
  saveOneToFirestore,
  openReview,

  // smart comments
  openSmart,
  closeSmart,
  smartOpen,
  smartLoading,
  smartComments,
  chosenSuggestion,
  setChosenSuggestion,
  sendChosen,
  sendError,
  sending,
  smartError,

  // review dialog state + submit
  reviewOpen, // not used here but kept for parity
  closeReview, // not used here but kept for parity
  reviewVerdict, // not used here but kept for parity
  reviewReason,  // not used here but kept for parity
  setReviewReason, // not used here but kept for parity
  submitReview, // not used here but kept for parity
  reviewSubmitting, // not used here but kept for parity

  // context flags for labeling
  isLCSelected,
  isRoofingSelected,

  // computed tag groups
  ownerTags,
  prospectorTags,

  // helpers used in rendering
  cleanFbNoise,
}) {
  // 🚫 Prevent unneeded re-renders from parent by memoizing a stable key
  const leadKey = React.useMemo(
    () => selected?._id || selected?.id || selected?.postUrl || '',
    [selected]
  );
const summaryKeyOf = (s) => {
  if (!s) return '';
  // Only track what affects rendering. Trim to avoid whitespace flaps.
  const v = (s.verdict || '').trim();
  const t = (s.text || '').trim();
  // If you want stronger hashing, plug a tiny hash; this string is fine.
  return `${v}::${t.length}::${t.slice(0, 200)}`; // first 200 chars as a cheap content fingerprint
};
  // Keep previous timeline and AI summary while switching leads
  const [localTimeline, setLocalTimeline] = React.useState([]);
  const [localSummary, setLocalSummary] = React.useState(aiSummary);
   const lastSummaryKeyRef = React.useRef(summaryKeyOf(aiSummary));
const [dmLoading, setDmLoading] = React.useState(false);
const [dmError, setDmError] = React.useState('');
const [dmLastText, setDmLastText] = React.useState('');


 const fetchTimelineRef = React.useRef(fetchTimeline);
 React.useEffect(() => { fetchTimelineRef.current = fetchTimeline; }, [fetchTimeline]);
// ADD NEAR THE TOP OF DetailsModal
// REPLACE your existing displayAiSummary memo with this:
const displayAiSummary = React.useMemo(() => {
  const source = localSummary?.text ?? aiSummary;
  if (!source) return '';

  const s = Array.isArray(source) ? source.join('\n') : String(source);
  const parts = s.split(/\n{1,}/).map(p => p.trim()).filter(Boolean);

  const uniq = [];
  const seen = new Set();
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      uniq.push(p);
    }
  }
  return uniq.join('\n');
}, [localSummary?.text, aiSummary]);
  // Fetch timeline on open/lead change; keepMounted prevents unmount flicker
  React.useEffect(() => {
    if (!open || !selected) return;
    let active = true;

    (async () => {
      try {
        setTimelineLoading?.(true);
       const t = await fetchTimelineRef.current?.(selected);
        if (active) setLocalTimeline(t || []);
      } finally {
        setTimelineLoading?.(false);
      }
    })();

    return () => {
      active = false;
    };
 }, [open, leadKey]); // ✅ no function deps
  // Keep AI summary from flashing between null and fetched
 React.useEffect(() => {
   // Optional: ignore intermediate stream updates
   if (aiSummaryLoading) return;
   const nextKey = summaryKeyOf(aiSummary);
   if (nextKey && nextKey !== lastSummaryKeyRef.current) {
    lastSummaryKeyRef.current = nextKey;
     setLocalSummary(aiSummary);
   }
 }, [aiSummaryLoading, aiSummary?.text, aiSummary?.verdict]);
async function handleDmUser() {
  if (!selected?.postUrl || !selected?.text) return;
  setDmLoading(true);
  setDmError('');
  setDmLastText('');

  try {
    const resp = await fetch('http://localhost:4001/api/nd/dm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        postUrl: selected.postUrl,
        postText: selected.text,
        market: selected.market || null,
        vertical: selected.subtype || selected.type || null,
        // If you want the backend to log timeline, you can pass leadId and let it do it.
        leadId: selected._id || selected.id || null
      })
    });
    const data = await resp.json();
    if (!data.ok) {
      setDmError(data.error || 'Failed to send DM.');
    } else {
      setDmLastText(data.dmText || '');
      // Optional: log locally too
      await logEvent?.(selected, 'dm_sent', {
        postUrl: selected.postUrl,
        text: data.dmText,
        stdout: data.stdout,
        stderr: data.stderr,
        code: data.exitCode
      });
      // Refresh timeline
      setTimelineLoading?.(true);
      const t = await fetchTimeline?.(selected);
      setTimelineLoading?.(false);
      setLocalTimeline(Array.isArray(t) ? t : []);
    }
  } catch (e) {
    setDmError(e?.message || String(e));
  } finally {
    setDmLoading(false);
  }
}
  // Small render helpers
  const postedChip = selected?.postedAt
    ? (
        <Chip
          size="small"
          label={`Posted: ${new Date(selected.postedAt).toLocaleString()}`}
          sx={{ bgcolor: '#eef2ff' }}
        />
      )
    : null;

  const capturedChip = selected?.capturedAt
    ? (
        <Chip
          size="small"
          label={`Captured: ${new Date(selected.capturedAt).toLocaleString()}`}
          sx={{ bgcolor: '#E5E7EB' }}
        />
      )
    : null;

  const verticalChip =
    Array.isArray(selected?.tags) &&
    selected.tags.some(t => /^VERT:/i.test(String(t?.id))) ? (
      <Chip
        size="small"
        label={selected.tags.find(t => /^VERT:/i.test(String(t?.id)))?.label || 'vertical'}
        sx={{ bgcolor: '#f0fdf4' }}
      />
    ) : null;

  return (
    <Dialog open={open} onClose={closeDetails} maxWidth="md" fullWidth keepMounted>
      <DialogTitle sx={{ pr: 6 }}>
        Post details
        <IconButton onClick={closeDetails} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {!!selected && (
          <Container maxWidth="md" sx={{ py: 2.5 }}>
            {/* Header: author + timestamp/permalink */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 0.25, lineHeight: 1.25 }}>
                {selected.author}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {postedChip}
                {capturedChip}
                {verticalChip}
              </Stack>
            </Box>

            {/* Body text */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#ffffff' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {cleanFbNoise?.(selected.text) ?? selected.text ?? 'No content found'}
              </Typography>

              {selected.lang === 'es' && selected.translatedEn && (
                <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed #e5e7eb' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25 }}>
                    Auto-translation
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', color: '#374151' }}>
                    {selected.translatedEn}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* AI Summary (from ChatGPT) */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                mb: 2,
                bgcolor: '#ffffff',
                borderColor:
                  localSummary?.verdict === 'good'
                    ? '#86efac'
                    : localSummary?.verdict === 'medium'
                    ? '#fde68a'
                    : localSummary?.verdict === 'bad'
                    ? '#fecaca'
                    : 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <AutoAwesomeIcon sx={{ color: '#6b7280' }} />
                <Typography variant="subtitle2" sx={{ color: '#374151' }}>
                  AI Summary
                </Typography>
                {localSummary?.verdict && (
                  <Chip
                    size="small"
                    label={
                      localSummary.verdict === 'good'
                        ? '✅ Good Lead'
                        : localSummary.verdict === 'medium'
                        ? '⚠️ Medium Lead'
                        : localSummary.verdict === 'bad'
                        ? '❌ Rejected'
                        : String(localSummary.verdict)
                    }
                    sx={{
                      ml: 'auto',
                      fontWeight: 700,
                      bgcolor:
                        localSummary.verdict === 'good'
                          ? '#d1fae5'
                          : localSummary.verdict === 'medium'
                          ? '#fef9c3'
                          : localSummary.verdict === 'bad'
                          ? '#fee2e2'
                          : '#e5e7eb',
                    }}
                  />
                )}
              </Stack>

              {aiSummaryLoading && <LinearProgress sx={{ mb: 1 }} />}

              {!!aiSummaryError && (
                <Typography variant="body2" color="error">
                  {aiSummaryError}
                </Typography>
              )}

{aiSummaryLoading && <LinearProgress sx={{ mb: 1 }} />}

{!!aiSummaryError && (
  <Typography variant="body2" color="error">
    {aiSummaryError}
  </Typography>
)}

{!aiSummaryLoading && !aiSummaryError && displayAiSummary && (
  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
    {displayAiSummary}
  </Typography>
)}

{!aiSummaryLoading && !aiSummaryError && !displayAiSummary && (
  <Typography variant="body2" color="text.secondary">
    No AI summary available.
  </Typography>
)}
            </Paper>

            {/* Tree Service — Generate Script */}
            {canGenerateScript && (
              <Paper variant="outlined" sx={{ p: 1.25, mb: 2, bgcolor: '#ffffff', borderColor: '#e5e7eb' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#374151' }}>
                    Call Script (Tree Service)
                  </Typography>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleGenerateScript}
                    disabled={callScriptLoading}
                    sx={{ ml: 'auto', textTransform: 'none' }}
                  >
                    {callScriptLoading ? 'Generating…' : 'Generate script'}
                  </Button>
                </Stack>

                {callScriptLoading && <LinearProgress sx={{ mb: 1 }} />}

                {!!callScriptError && (
                  <Typography variant="body2" color="error">
                    {callScriptError}
                  </Typography>
                )}

                {!!callScript && (
                  <>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                      {callScript}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => copyToClipboard?.(callScript)} sx={{ textTransform: 'none' }}>
                        Copy to clipboard
                      </Button>
                    </Stack>
                  </>
                )}

                {!callScript && !callScriptLoading && !callScriptError && (
                  <Typography variant="body2" color="text.secondary">
                    Click “Generate script” to create a friendly call opener tailored to this post.
                  </Typography>
                )}
              </Paper>
            )}

            {/* Scores & Class */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    Scores
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={`${isLCSelected ? 'Need' : 'Owner'} ${selected?.ownerScore ?? 0}`}
                      sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={`${isLCSelected ? 'Vendor' : 'Prospector'} ${selected?.prospectorScore ?? 0}`}
                      sx={{ bgcolor: '#fee2e2', color: '#7f1d1d', fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={String(selected?.classification ?? 'uncertain')}
                      sx={{
                        bgcolor: selected?.classification === 'homeowner' ? '#dcfce7' : '#fde68a',
                        color: selected?.classification === 'homeowner' ? '#065f46' : '#7c2d12',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                      }}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    Meta
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip size="small" label={selected?.platform || 'facebook'} sx={{ bgcolor: '#DBEAFE' }} />
                    {selected?.market && <Chip size="small" label={selected.market} sx={{ bgcolor: '#FEF3C7' }} />}
                    {selected?.type && <Chip size="small" label={selected.type} sx={{ bgcolor: '#E9D5FF' }} />}
                    {selected?.subtype && <Chip size="small" label={selected.subtype} sx={{ bgcolor: '#c7d2fe' }} />}
                    {selected?.capturedAt && (
                      <Chip size="small" label={new Date(selected.capturedAt).toLocaleString()} sx={{ bgcolor: '#E5E7EB' }} />
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Review summary (if present) */}
            {(selected?.review?.verdict || selected?.review?.reason) && (
              <Paper variant="outlined" sx={{ p: 1.25, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                  Review
                </Typography>
                <Stack spacing={0.5}>
                  {selected?.review?.verdict && (
                    <Chip
                      size="small"
                      label={selected.review.verdict === 'up' ? 'Approved' : 'Rejected'}
                      sx={{
                        width: 'fit-content',
                        bgcolor: selected.review.verdict === 'up' ? '#d1fae5' : '#fee2e2',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {selected?.review?.reason && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selected.review.reason}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Backend AI classification */}
            {(selected?.verdict || selected?.reason) && (
              <Paper variant="outlined" sx={{ p: 1.25, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                  Lead Classification
                </Typography>
                <Stack spacing={0.5}>
                  {selected?.verdict && (
                    <Chip
                      size="small"
                      label={
                        selected.verdict === 'good'
                          ? '✅ Good Lead'
                          : selected.verdict === 'medium'
                          ? '⚠️ Medium Lead'
                          : '❌ Rejected'
                      }
                      sx={{
                        width: 'fit-content',
                        bgcolor:
                          selected.verdict === 'good'
                            ? '#d1fae5'
                            : selected.verdict === 'medium'
                            ? '#fef9c3'
                            : '#fee2e2',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {selected?.reason && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selected.reason}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Signals */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    Distress Signals
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {ownerTags.length > 0 ? (
                      ownerTags.map(t => (
                        <Chip key={`own-${t.id}`} size="small" label={t.label} sx={{ bgcolor: t.color }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None detected.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    {isLCSelected ? 'Vendor / Anti-Signals' : isRoofingSelected ? 'Roofing Vendor / Spam' : 'Prospector / Anti-Signals'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {prospectorTags.length > 0 ? (
                      prospectorTags.map(t => (
                        <Chip key={`pro-${t.id}`} size="small" label={t.label} sx={{ bgcolor: t.color }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None detected.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Actions */}
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: '#fbfbfb' }}>
              <Grid container spacing={1.25}>
                {/* Navigation */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Navigate
                  </Typography>
                  <ButtonGroup orientation="horizontal" fullWidth sx={{ mt: 0.75 }}>
                    <Button
                      variant="outlined"
                      disabled={!selected?.profileUrl}
                      onClick={() => selected?.profileUrl && window.open(selected.profileUrl, '_blank')}
                    >
                      View Profile
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!selected?.postUrl}
                      onClick={() => selected?.postUrl && window.open(selected.postUrl, '_blank')}
                    >
                      View Post
                    </Button>
                  </ButtonGroup>
                  
                  <ButtonGroup orientation="horizontal" fullWidth sx={{ mt: 0.75 }}>
  <Button size="small" variant="contained" onClick={() => openSmart?.()}>
    Smart Comment
  </Button>
</ButtonGroup>
<ButtonGroup orientation="horizontal" fullWidth sx={{ mt: 0.75 }}>
  <Button
    size="small"
    variant="outlined"
    disabled={!selected?.postUrl || !selected?.text || dmLoading}
    onClick={handleDmUser}
  >
    {dmLoading ? 'Sending DM…' : 'DM User'}
  </Button>
</ButtonGroup>

{dmError && (
  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
    {dmError}
  </Typography>
)}

{!dmError && dmLastText && (
  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#374151' }}>
    Sent: “{dmLastText}”
  </Typography>
)}
                </Grid>

                {/* Lead actions */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Lead
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 0.75 }}>
                    <Grid item xs={12}>
                      <Button fullWidth disabled variant="outlined" onClick={() => saveOneToFirestore?.(selected)}>
                        Saved to DB
                      </Button>
                    </Grid>

                    {/* Danger row */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          disabled={!selected || deleting}
                          onClick={() => handleDeleteFromDB?.(selected)}
                        >
                          {deleting ? 'Deleting…' : 'Delete from DB'}
                        </Button>
                      </Stack>
                    </Grid>

                    {/* Brad Leads toggle */}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant={bradKeys?.has?.(makeKey?.(selected)) ? 'outlined' : 'contained'}
                        color="primary"
                        onClick={e => toggleBrad?.(selected, e)}
                      >
                        {bradKeys?.has?.(makeKey?.(selected)) ? 'Remove from Brad Leads' : 'Add to Brad Leads'}
                      </Button>
                    </Grid>

                    {/* Skip Trace */}
                    {!isLCSelected && (
                      <Grid item xs={12}>
                        <Button fullWidth color="secondary" variant="contained" onClick={handleOpenSkipTrace}>
                          Skip Trace
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {/* Review */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Review
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      startIcon={<ThumbUpAltIcon />}
                      onClick={() => openReview?.('up')}
                    >
                      Approve
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<ThumbDownAltIcon />}
                      onClick={() => openReview?.('down')}
                    >
                      Reject
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Timeline & Notes */}
            <Paper variant="outlined" sx={{ p: 1.25, mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#374151' }}>
                Timeline & Notes
              </Typography>

              {/* Add note */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Add a note"
                    placeholder="e.g., Left voicemail, follow up tomorrow."
                    value={noteText}
                    onChange={e => setNoteText?.(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!noteText?.trim?.() || !selected}
                    onClick={async () => {
                      const text = noteText?.trim?.();
                      if (!text || !selected) return;
                      await logEvent?.(selected, 'note', { note: text });
                      setNoteText?.('');
                      setTimelineLoading?.(true);
                      const t = await fetchTimeline?.(selected);
                      setTimelineLoading?.(false);
                      setLocalTimeline(Array.isArray(t) ? t : []);
                    }}
                  >
                    Add Note
                  </Button>
                </Grid>
              </Grid>

              {/* Events list */}
              <Box sx={{ maxHeight: 260, overflow: 'auto', pr: 1 }}>
                {timelineLoading ? (
                  <LinearProgress />
                ) : localTimeline.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet.
                  </Typography>
                ) : (
                  <Stack spacing={2} sx={{ pl: 3 }}>
                    {localTimeline.map(e => {
                      const type = e.type || 'event';
                      const payload = e.payload || {};
                      const serverDate = e.at?.toDate ? e.at.toDate() : e.at ? new Date(e.at) : null;
                      const sourceOverride = payload.sourceTs ? new Date(payload.sourceTs) : null;
                      const when = sourceOverride || serverDate;
                      const whenStr = when ? when.toLocaleString() : '—';

                      let icon = null,
                        label = '';
                      switch (type) {
                        case 'captured':
                          icon = <AddCircleOutlineIcon fontSize="small" sx={{ color: '#3b82f6' }} />;
                          label = 'Captured';
                          break;
                        case 'call':
                          icon = <PhoneInTalkIcon fontSize="small" sx={{ color: '#10b981' }} />;
                          label = `Called ${payload.number ? ` ${payload.number}` : ''}`.trim();
                          break;
                        case 'review_up':
                          icon = <ThumbUpAltIcon fontSize="small" sx={{ color: '#16a34a' }} />;
                          label = 'Approved';
                          break;
                        case 'review_down':
                          icon = <ThumbDownAltIcon fontSize="small" sx={{ color: '#dc2626' }} />;
                          label = 'Rejected';
                          break;
                        case 'note':
                          icon = <NoteAltIcon fontSize="small" sx={{ color: '#7c3aed' }} />;
                          label = payload.note || 'Note';
                          break;
                        case 'saved':
                          icon = <BookmarkBorderIcon fontSize="small" sx={{ color: '#0ea5e9' }} />;
                          label = 'Saved lead';
                          break;
                        case 'unsaved':
                          icon = <BookmarkBorderIcon fontSize="small" sx={{ color: '#94a3b8' }} />;
                          label = 'Unsaved lead';
                          break;
                        default:
                          icon = <AccessTimeIcon fontSize="small" sx={{ color: '#6b7280' }} />;
                          label = type;
                      }

                      return (
                        <Box key={e.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          {/* circle with icon */}
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                              border: '2px solid #d1d5db',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {icon}
                          </Box>

                          {/* content */}
                          <Box sx={{ flex: 1, pt: 0.25 }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.3, mb: 0.25 }}>
                              {label}
                            </Typography>

                            {(type === 'review_up' || type === 'review_down') && payload?.reason && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mb: 0.25,
                                  fontStyle: 'italic',
                                  color: '#374151',
                                }}
                              >
                                Reason: {payload.reason}
                              </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary">
                              {whenStr}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Paper>
          </Container>
        )}
      </DialogContent>
    </Dialog>
  );
});

 export default React.memo(DetailsModal, (prev, next) => {
  const prevId = prev.selected?._id ?? prev.selected?.id ?? prev.selected?.postUrl ?? null;
  const nextId = next.selected?._id ?? next.selected?.id ?? next.selected?.postUrl ?? null;
const prevSumKey = (prev.aiSummaryLoading ? '' : (prev.aiSummary && ((prev.aiSummary.verdict||'').trim()+ '::' + ((prev.aiSummary.text||'').trim().length) + '::' + (prev.aiSummary.text||'').trim().slice(0,200)))) || '';
const nextSumKey = (next.aiSummaryLoading ? '' : (next.aiSummary && ((next.aiSummary.verdict||'').trim()+ '::' + ((next.aiSummary.text||'').trim().length) + '::' + (next.aiSummary.text||'').trim().slice(0,200)))) || '';

   return (
     prev.open === next.open &&
     prevId === nextId &&
       prev.aiSummaryLoading === next.aiSummaryLoading &&   // still allow the final flip to false
     prevSumKey === nextSumKey &&                         // only re-render when content changes
     prev.timelineLoading === next.timelineLoading
   );
 });